import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tag, Button, Spin, Breadcrumb, Row, Col, Typography, Divider, Image } from 'antd';
import { Helmet } from 'react-helmet-async';
import {
    ShoppingCartOutlined,
    SafetyOutlined,
    HomeOutlined,
    WhatsAppOutlined,
    TruckOutlined,
    PhoneOutlined
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import truckTireService, { TruckTire } from '../../services/truckTireService';
import { getTruckTireInquiryWhatsAppUrl } from '../../utils/productInquiry';
import { PRODUCT_IMAGE_PLACEHOLDER } from '../../utils/productImage';

const { Title, Text } = Typography;

const TruckTireDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<TruckTire | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                if (!id) return;

                const response = await truckTireService.getTruckTireById(id);
                setProduct(response.data);

                window.scrollTo(0, 0);
            } catch (error: any) {
                console.error('Kamyon lastiği detayı yüklenemedi:', error);
                navigate('/kamyon-lastikleri');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProduct();
        }
    }, [id, navigate]);

    if (loading) {
        return (
            <MainLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <Spin size="large" tip="Kamyon lastiği detayları yükleniyor..." />
                </div>
            </MainLayout>
        );
    }

    if (!product) {
        return null;
    }

    // Ürün resmini al - farklı yolları dene
    const getProductImage = () => {
        if (product.UrunResim) {
            const cleanImageName = product.UrunResim.split('?')[0];
            // Önce products klasöründen dene, sonra genel uploads'tan
            return `/uploads/products/${cleanImageName}`;
        }
        return PRODUCT_IMAGE_PLACEHOLDER;
    };

    const imageUrl = getProductImage();

    // Format price
    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    // WhatsApp sipariş linki
    const whatsappMessage = `Merhaba, ${product.MalzemeAdi} ürününü sipariş etmek istiyorum. Ürün Kodu: ${product.MalzemeKodu}`;
    const hidePrices = product.hidePrices === true;
    const whatsappUrl = hidePrices
        ? getTruckTireInquiryWhatsAppUrl(product, `https://www.sakaryalastik.com/kamyon-lastikleri/${product.UrunID}`)
        : `https://wa.me/905555555555?text=${encodeURIComponent(whatsappMessage)}`;

    return (
        <MainLayout>
            <Helmet>
                <title>{product.MalzemeAdi} | Sakarya Lastik - Kamyon Lastiği</title>
                <meta name="description" content={`${product.Markasi} ${product.MalzemeAdi} kamyon lastiği fiyatı ve özellikleri.`} />
            </Helmet>

            <div className="container mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <Breadcrumb className="mb-6">
                    <Breadcrumb.Item href="/">
                        <HomeOutlined /> Ana Sayfa
                    </Breadcrumb.Item>
                    <Breadcrumb.Item href="/kamyon-lastikleri">
                        <TruckOutlined /> Kamyon Lastikleri
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{product.MalzemeAdim}</Breadcrumb.Item>
                </Breadcrumb>

                <Row gutter={[32, 32]}>
                    {/* Sol: Ürün Görseli */}
                    <Col xs={24} lg={10}>
                        <Card className="shadow-lg overflow-hidden">
                            <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-8">
                                {product.StokMiktari > 0 && (
                                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-green-500 text-white text-xs font-semibold shadow-md z-10">
                                        {product.StokMiktari} Stokta
                                    </div>
                                )}

                                {/* Ana Ürün Görseli */}
                                <div className="flex justify-center items-center min-h-[400px]">
                                    <Image
                                        src={imageUrl}
                                        alt={product.MalzemeAdi}
                                        className="max-w-full max-h-[400px] object-contain drop-shadow-2xl"
                                        placeholder={
                                            <div className="w-full h-[400px] bg-gray-200 flex items-center justify-center">
                                                <TruckOutlined className="text-6xl text-gray-400" />
                                            </div>
                                        }
                                        fallback={PRODUCT_IMAGE_PLACEHOLDER}
                                        onError={() => {
                                            console.log('Resim yükleme hatası:', imageUrl);
                                        }}
                                    />
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* Sağ: Ürün Detayları */}
                    <Col xs={24} lg={14}>
                        <Card className="shadow-lg">
                            <div className="space-y-6">
                                {/* Başlık ve Marka */}
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <Tag color="blue" className="text-sm font-medium px-3 py-1">
                                            {product.Markasi}
                                        </Tag>
                                        <Tag color="purple" className="text-sm font-medium px-3 py-1">
                                            {product.Grup}
                                        </Tag>
                                    </div>
                                    <Title level={1} className="!mb-2 !text-2xl lg:!text-3xl">
                                        {product.MalzemeAdi}
                                    </Title>
                                    <Text className="text-gray-600 text-lg">
                                        Ürün Kodu: {product.MalzemeKodu}
                                    </Text>
                                </div>

                                <Divider />

                                {/* Fiyat Bilgileri */}
                                {hidePrices ? (
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<WhatsAppOutlined />}
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-full bg-green-600 hover:bg-green-700 border-none h-14 text-base font-semibold"
                                    >
                                        Lütfen Fiyatlar İçin Bize Ulaşın
                                    </Button>
                                ) : (
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="text-center">
                                                <Text className="block text-sm text-gray-500 mb-1">Havale Fiyatı</Text>
                                                <Title level={3} className="!text-green-600 !mb-0">
                                                    {formatPrice(product.HavaleFiyati)}
                                                </Title>
                                                <Text className="text-xs text-green-600">
                                                    %{product.HavaleIndirimi} İndirim
                                                </Text>
                                            </div>
                                            <div className="text-center">
                                                <Text className="block text-sm text-gray-500 mb-1">Tek Çekim</Text>
                                            <Title level={4} className="!text-blue-600 !mb-0">
                                                {formatPrice(product.TekCekimFiyati)}
                                            </Title>
                                            <Text className="text-xs text-blue-600">
                                                %{product.TekCekimIndirimi} İndirim
                                            </Text>
                                        </div>
                                        <div className="text-center">
                                            <Text className="block text-sm text-gray-500 mb-1">Taksitli</Text>
                                            <Title level={4} className="!text-purple-600 !mb-0">
                                                {formatPrice(product.TaksitFiyati)}
                                            </Title>
                                            <Text className="text-xs text-purple-600">
                                                %{product.TaksitIndirimi} İndirim
                                            </Text>
                                        </div>
                                        </div>
                                        <div className="text-center mt-4 pt-4 border-t border-gray-200">
                                            <Text className="text-sm text-gray-500">
                                                Liste Fiyatı: <span className="line-through">{formatPrice(product.ListeFiyati)}</span>
                                            </Text>
                                        </div>
                                    </div>
                                )}

                                <Divider />

                                {/* Sipariş Butonları */}
                                <div className="space-y-4">
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<WhatsAppOutlined />}
                                        onClick={() => window.open(whatsappUrl, '_blank', 'noopener,noreferrer')}
                                        className="w-full bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600 h-12 text-lg font-semibold"
                                    >
                                        {hidePrices ? 'Lütfen Fiyatlar İçin Bize Ulaşın' : 'WhatsApp ile Sipariş Ver'}
                                    </Button>

                                    <div className="grid grid-cols-2 gap-4">
                                        <Button
                                            size="large"
                                            icon={<PhoneOutlined />}
                                            onClick={() => window.open('tel:+905555555555', '_self')}
                                            className="h-12 font-medium"
                                        >
                                            Telefon ile Ara
                                        </Button>
                                        <Button
                                            size="large"
                                            icon={<ShoppingCartOutlined />}
                                            className="h-12 font-medium"
                                        >
                                            Sepete Ekle
                                        </Button>
                                    </div>
                                </div>

                                {/* Özellikler */}
                                <div>
                                    <Title level={4} className="flex items-center gap-2 !mb-4">
                                        <SafetyOutlined className="text-blue-500" />
                                        Ürün Özellikleri
                                    </Title>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">Araç Grubu</Text>
                                            <div className="font-semibold text-gray-800">{product.AracGrubu}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">Kategori</Text>
                                            <div className="font-semibold text-gray-800">{product.Kategori}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">Birim</Text>
                                            <div className="font-semibold text-gray-800">{product.Birim}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">KDV Oranı</Text>
                                            <div className="font-semibold text-gray-800">%{product.Kdv}</div>
                                        </div>
                                    </div>

                                    {product.Dod && (
                                        <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">Üretim Yılı</Text>
                                            <div className="font-semibold text-gray-800">{product.Dod}</div>
                                        </div>
                                    )}

                                    {product.Not && (
                                        <div className="mt-4 bg-yellow-50 p-3 rounded-lg">
                                            <Text className="text-sm text-gray-500">Not</Text>
                                            <div className="font-semibold text-gray-800">{product.Not}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </MainLayout>
    );
};

export default TruckTireDetailPage;
